// Custom TS errors taken from: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#example
import { tracer } from "@/app/tracing";
import { translateWithParams } from "@/plugins/i18n";
import type Guid from "@/app/types/common/guid";
import type { EnumBmEntityType } from "../services/generated/enums/enumBmEntityType.generated";

// TODO: Translate these GRPC status codes
// Used to display error type to user.
export const errorStatusCodeToTextLookup: { [code: number]: string } = {
  0: "Success",
  1: "Cancelled",
  2: "Unknown",
  3: "Invalid Argument",
  4: "Deadline Exceeded",
  5: "Not Found",
  6: "Already Exists",
  7: "Permission Denied",
  8: "Resource Exhausted",
  9: "Failed Precondition",
  10: "Aborted",
  11: "Out of Range",
  12: "Unimplemented",
  13: "Internal",
  14: "Unavailable",
  15: "Data Loss",
  16: "Unauthenticated",
};

export const UnauthenticatedStatusCode = 16;

interface ErrorJson {
  apiExceptionStatus: string;
  apiExceptionStatusCode: number;
  apiExceptionReason: string;
  apiExceptionMessage: string;
  apiExceptionStackTrace: string;
}

export class BrewManError extends Error {
  traceId?: string;

  constructor(message?: string, originator?: Error) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    if (originator) {
      this.stack = originator.stack;
    }

    this.traceId = tracer.getCurrentTrace()?.traceId || undefined;
  }
}

export class BrewManApiError extends Error {
  static fromBm7ErrorJson(
    errorStatusCode: number,
    errorObject: object,
    endpoint: string
  ) {
    try {
      const errorDetails = errorObject as ErrorJson;
      if (errorDetails) {
        return new BrewManApiError(
          "",
          errorDetails.apiExceptionStatusCode,
          errorDetails.apiExceptionMessage,
          errorDetails.apiExceptionStackTrace,
          errorDetails.apiExceptionReason,
          endpoint
        );
      }
    } catch {
      // intentionally empty
    }

    return new BrewManApiError(
      "",
      errorStatusCode,
      JSON.stringify(errorObject),
      undefined,
      undefined,
      endpoint
    );
  }

  constructor(
    public name: string,
    public code: number,
    public message: string,
    public serverException?: string,
    public reasonCode?: string,
    public endpoint?: string
  ) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
  }

  public get isDeadlineExceeded() {
    return this.code === 4;
  }

  public get isAlreadyExists() {
    return this.code === 6;
  }

  public get isPermissionDenied() {
    return this.code === 7;
  }

  public get isUnauthenticated() {
    return this.code === 16;
  }
}

// Wherever we use `getById` or alike, we should throw an error if we can't find
// what we would expect to be there. Use this to provide a consistent error.
export class BrewManCannotFindEntityError extends Error {
  constructor(entityType: EnumBmEntityType, id: Guid | null) {
    const message = translateWithParams("cannot_find_entity", {
      entity: entityType.translation,
      id: id?.toString() ?? "",
    }).toString();

    super(message);
  }
}
